import { useEffect, useRef, useState } from 'react';
import { fullMotion } from '@/lib/experience';

/**
 * A figure that counts up to its value when it first shows (e.g. "AED 1,179,203.66", "(AED 725,159.73)", "6.7%").
 * Only the first number in the text moves; its decimals and thousands separators are kept, and the final text is
 * exactly the one given. Plain text with motion off or anything that is not a number.
 */
export function CountUp({ value, ms = 750 }: { value: string; ms?: number }) {
  const m = /-?\d[\d,]*(\.\d+)?/.exec(value);
  const [shown, setShown] = useState(() => (m && fullMotion() ? render(value, m, 0) : value));
  const done = useRef(false);
  useEffect(() => {
    if (!m || !fullMotion() || done.current) {
      setShown(value);
      return;
    }
    done.current = true;
    const target = Number(m[0].replace(/,/g, ''));
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      const eased = 1 - (1 - k) ** 3;
      setShown(k < 1 ? render(value, m, target * eased) : value);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{shown}</>;
}

function render(text: string, m: RegExpExecArray, n: number) {
  const decimals = m[1] ? m[1].length - 1 : 0;
  const grouped = m[0].includes(',');
  const s = n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouped });
  return text.slice(0, m.index) + s + text.slice(m.index + m[0].length);
}

/**
 * A figure that rolls smoothly to each new value (form totals as lines are typed). The first value counts up like
 * `CountUp`; later changes roll from the previous figure. The final text is always exactly the one given.
 */
export function Tween({ value, ms = 380 }: { value: string; ms?: number }) {
  const m = /-?\d[\d,]*(\.\d+)?/.exec(value);
  const target = m ? Number(m[0].replace(/,/g, '')) : NaN;
  const from = useRef<number>(0);
  const [shown, setShown] = useState(() => (m && fullMotion() ? render(value, m, 0) : value));
  useEffect(() => {
    if (!m || !fullMotion() || !Number.isFinite(target)) {
      setShown(value);
      from.current = target;
      return;
    }
    const start = from.current;
    if (start === target) {
      setShown(value);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      const eased = 1 - (1 - k) ** 3;
      const n = start + (target - start) * eased;
      from.current = n;
      setShown(k < 1 ? render(value, m, n) : value);
      if (k < 1) raf = requestAnimationFrame(step);
      else from.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{shown}</>;
}
